import { skyStageArtVariant } from './134-sky-asset-manifest.mjs';

/** Presentation-only stage loading coordinator. A renderer lease owns the actual
 * resources; this owner deduplicates requests, preserves errors until an explicit
 * retry, and discards completions from replaced/disposed renderer generations. */
export function createSkyPreload({ renderer, changed, accept = () => {} }) {
  let generation = 0;
  const stages = new Map();
  const keyFor = (state) => `${state.sector}:${skyStageArtVariant(state) ?? ''}`;
  function read(state) {
    return stages.get(keyFor(state))?.status ?? 'idle';
  }
  function request(state, retry = false, briefing = false, preloadOnly = false) {
    const key = keyFor(state);
    const previous = stages.get(key);
    const owner = renderer();
    // A new arrival rebuilds its released projection; live frames retain the cache.
    if (
      previous &&
      !(previous.preloadOnly && !preloadOnly && previous.status !== 'error') &&
      !(retry && previous.status === 'error') &&
      !(briefing && previous.status === 'ready')
    ) {
      if (previous.status === 'ready') {
        try {
          accept(state, owner);
        } catch {
          previous.status = 'error';
        }
      }
      return previous.status;
    }
    if (!owner) return 'idle';
    const record = { status: owner.prepareStage ? 'loading' : 'ready', preloadOnly };
    stages.set(key, record);
    if (!owner.prepareStage) {
      try {
        accept(state, owner);
      } catch {
        record.status = 'error';
      }
      return record.status;
    }
    const epoch = generation;
    const settle = (status) => {
      if (epoch !== generation || stages.get(key) !== record) return;
      if (status === 'ready') {
        try {
          accept(snapshot, owner);
        } catch {
          status = 'error';
        }
      }
      record.status = status;
      changed();
    };
    // Snapshot the sector: domain transitions mutate the live run in place.
    const snapshot = { ...state };
    try {
      Promise.resolve(owner.prepareStage(snapshot, { preloadOnly })).then(
        () => settle('ready'),
        () => settle('error'),
      );
    } catch {
      record.status = 'error';
    }
    return record.status;
  }
  function reset() {
    generation++;
    stages.clear();
  }
  return { read, request, reset };
}
